import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Loader2, Brain, Sparkles, MessageSquare, Calendar, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function AdminAIUsage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/admin/ai-usage', { params });
      setData(res.data);
    } catch {
      console.error('Failed to load AI usage');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading && !data) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" style={{ color: '#195259' }} /></div>;
  }

  const kpis = [
    { label: 'Total AI Calls', value: data?.total_calls || 0, icon: Brain, bg: '#195259' },
    { label: 'AI Builder', value: data?.builder_calls || 0, icon: Sparkles, bg: '#FF9A00' },
    { label: 'AI Chat', value: data?.chat_calls || 0, icon: MessageSquare, bg: '#195259' },
  ];

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="admin-ai-usage">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#195259' }}>AI Usage</h1>
          <p className="text-sm mt-1" style={{ color: '#6D6E71' }}>Monitor AI Builder and AI Chat usage across all creators</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap" style={{ color: '#6D6E71' }}>From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36 h-9 text-sm" data-testid="ai-date-from" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap" style={{ color: '#6D6E71' }}>To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36 h-9 text-sm" data-testid="ai-date-to" />
          </div>
          {(dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs" style={{ color: '#FF9A00' }}>Clear</Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {kpis.map(kpi => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: '#6D6E71' }}>{kpi.label}</p>
                    <p className="text-3xl font-bold mt-1" style={{ color: '#195259' }}>{kpi.value.toLocaleString()}</p>
                  </div>
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: kpi.bg }}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Daily Trend Chart */}
      {data?.daily_trend?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" style={{ color: '#195259' }} />
              <CardTitle className="text-base" style={{ color: '#195259' }}>Daily Usage Trend</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(25,82,89,0.12)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6D6E71' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6D6E71' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #195259', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="builder" name="AI Builder" fill="#FF9A00" radius={[4, 4, 0, 0]} />
                <Bar dataKey="chat" name="AI Chat" fill="#195259" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Usage by Creator */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base" style={{ color: '#195259' }}>Usage by Creator</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Creator</TableHead>
                <TableHead className="text-center">AI Builder</TableHead>
                <TableHead className="text-center">AI Chat</TableHead>
                <TableHead className="text-center">Total</TableHead>
                <TableHead>Last Used</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.creator_usage?.map(cu => (
                <TableRow key={cu.creator_id}>
                  <TableCell>
                    <p className="font-medium text-sm" style={{ color: '#195259' }}>{cu.creator_name || 'Unknown'}</p>
                    <p className="text-xs" style={{ color: '#6D6E71' }}>{cu.creator_email}</p>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge style={{ backgroundColor: '#FF9A00', color: 'white' }} className="text-xs">{cu.builder}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge style={{ backgroundColor: '#195259', color: 'white' }} className="text-xs">{cu.chat}</Badge>
                  </TableCell>
                  <TableCell className="text-center font-bold" style={{ color: '#195259' }}>{cu.total}</TableCell>
                  <TableCell className="text-xs" style={{ color: '#6D6E71' }}>
                    {cu.last_used ? new Date(cu.last_used).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                  </TableCell>
                </TableRow>
              ))}
              {(!data?.creator_usage?.length) && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12" style={{ color: '#6D6E71' }}>No AI usage recorded yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Calls */}
      {data?.recent_calls?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base" style={{ color: '#195259' }}>Recent AI Calls</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Creator</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Prompt</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent_calls.map(call => (
                  <TableRow key={call.id}>
                    <TableCell>
                      <p className="text-sm font-medium" style={{ color: '#195259' }}>{call.creator_name || 'Unknown'}</p>
                      <p className="text-xs" style={{ color: '#6D6E71' }}>{call.creator_email}</p>
                    </TableCell>
                    <TableCell>
                      <Badge style={{ backgroundColor: call.type === 'ai_builder' ? '#FF9A00' : '#195259', color: 'white' }} className="text-xs">
                        {call.type === 'ai_builder' ? 'Builder' : 'Chat'}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate text-sm" style={{ color: '#6D6E71' }}>{call.prompt}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap" style={{ color: '#6D6E71' }}>
                      {call.created_at ? new Date(call.created_at).toLocaleString('en-MY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
